import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Fade,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/lab';
import { Modal } from '../../components';
import { Save, Close } from '@mui/icons-material';
import { IEvent, IDropdownResponse } from '../../models';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { theme } from '../../styles';

interface AddEditEventModalProps {
  open: boolean;
  event?: IEvent;
  eventTypes?: IDropdownResponse[];
  onClose: () => void;
  onSave: (updatedEvent: IEvent) => void;
}

export const AddEditEventModal: FC<AddEditEventModalProps> = ({
  open,
  event,
  eventTypes = [],
  onClose,
  onSave,
}) => {
  const classes = useStyles();
  const [localEvent, setLocalEvent] = useState<IEvent>(event ? { ...event } : IEvent.getDefault());

  useEffect(() => {
    if (open) {
      setLocalEvent(event ? { ...event } : IEvent.getDefault());
    }
  }, [open, event]);

  const handleChange = (field: keyof IEvent, value: any) => {
    setLocalEvent(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDateChange = (field: 'eventDateStart' | 'eventDateEnd', date: Date | null) => {
    handleChange(field, date ? date.toISOString() : '');
  };

  const handleSave = () => {
    onSave(localEvent);
  };

  const isSaveDisabled =
    !localEvent.eventType || !localEvent.eventDateStart || !localEvent.eventDateEnd;

  return (
    <Modal
      maxWidth="sm"
      open={open}
      onClose={onClose}
      title={localEvent.eventId ? 'Edit Event' : 'Add Event'}
    >
      <Fade in={open}>
        <Box component="form" autoComplete="off" className={classes.form}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField
                label="Event Name"
                fullWidth
                variant="outlined"
                value={localEvent.eventName || ''}
                onChange={e => handleChange('eventName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                select
                label="Event Type"
                fullWidth
                variant="outlined"
                value={localEvent.eventType || ''}
                onChange={e => handleChange('eventType', e.target.value)}
              >
                {eventTypes.map(et => (
                  <MenuItem key={et.value} value={et.value}>
                    {et.description}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid container item xs={12} spacing={1}>
              <Grid item xs={6}>
                <DesktopDatePicker
                  label="Start Date *"
                  inputFormat="MM/dd/yyyy"
                  value={localEvent.eventDateStart ? new Date(localEvent.eventDateStart) : null}
                  onChange={date => handleDateChange('eventDateStart', date)}
                  maxDate={localEvent.eventDateEnd ? new Date(localEvent.eventDateEnd) : undefined}
                  renderInput={params => <TextField fullWidth variant="outlined" {...params} />}
                />
              </Grid>
              <Grid item xs={6}>
                <DesktopDatePicker
                  label="End Date *"
                  inputFormat="MM/dd/yyyy"
                  value={localEvent.eventDateEnd ? new Date(localEvent.eventDateEnd) : null}
                  onChange={date => handleDateChange('eventDateEnd', date)}
                  minDate={localEvent.eventDateStart ? new Date(localEvent.eventDateStart) : null}
                  renderInput={params => <TextField fullWidth variant="outlined" {...params} />}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localEvent.isVisible || false}
                    onChange={e => handleChange('isVisible', e.target.checked)}
                    color="primary"
                  />
                }
                label="Display Event"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Event Location"
                fullWidth
                variant="outlined"
                multiline
                minRows={3}
                value={localEvent.eventLocation || ''}
                onChange={e => handleChange('eventLocation', e.target.value)}
              />
            </Grid>
          </Grid>
          <Grid container spacing={1} justifyContent="flex-end" className={classes.buttonRow}>
            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSave}
                disabled={isSaveDisabled}
                startIcon={<Save />}
              >
                Save
              </Button>
            </Grid>
            <Grid item>
              <Button variant="contained" onClick={onClose} startIcon={<Close />}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Fade>
    </Modal>
  );
};

const useStyles = makeStyles<Theme>(() => ({
  form: {
    padding: theme.spacing(1),
    width: '100%',
    boxSizing: 'border-box',
  },
  buttonRow: {
    marginTop: theme.spacing(1),
  },
}));
